import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogClose,
} from '@/components/ui/dialog'
import { useEffect, useState } from 'react'
import { qrService } from '@/lib/qr'
import { useToast } from '@/hooks/use-toast'
import { createEthereumURI } from '@/utils'
import { Card } from '@/components/ui/card'
import { X, Loader2, ChevronLeft, Copy } from 'lucide-react'
import { Button } from './ui/button'

interface WalletQRModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	walletAddress: string
}

const WalletQRModal = ({
	open,
	onOpenChange,
	walletAddress,
}: WalletQRModalProps) => {
	const [walletAddressQR, setWalletAddressQR] = useState<string | null>(null)
	const { toast } = useToast()

	const generateWalletQR = async () => {
		try {
			const qrPayload = createEthereumURI(walletAddress)
			const qr = await qrService.generateQR(qrPayload)
			setWalletAddressQR(qr)
		} catch (error) {
			console.error('Error generating QR:', error)
			toast({
				title: 'QR Code Generation Failed',
				description: 'Unable to generate wallet QR.',
				variant: 'destructive',
			})
		}
	}

	const copyAddress = async (address: string) => {
		try {
			await navigator.clipboard.writeText(address)
			toast({
				title: 'Address Copied',
				description: 'Address has been copied to clipboard'
			})
		} catch (error) {
			toast({
				title: 'Error',
				description: 'Failed to copy address',
				variant: 'destructive'
			})
		}
	}

	useEffect(() => {
		if (open && walletAddress) {
			generateWalletQR()
		}
	}, [open, walletAddress])

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="h-full w-full p-5 overflow-y-auto sm:overflow-visible sm:rounded-lg rounded-lg bg-[#18181B] border-0">
				<DialogClose asChild>
					<button
						className="absolute top-4 left-5 max-[764px]:right-[-10px] w-8 h-8 flex items-center justify-center rounded-full text-[#ffffff] bg-white/10 hover:bg-white/20 transition p-0 m-0"
						aria-label="Close">
						<ChevronLeft className="h-5 w-5 text-[#ffffff]" />
					</button>
				</DialogClose>
				<DialogHeader>
					<DialogTitle className="text-[16px] leading-[18px] mt-1 mb-2 font-semibold font-figtree text-[#ffffff]">Wallet Address QR</DialogTitle>
					<DialogDescription className="text-xs leading-4 font-medium font-figtree mt-0 text-[#BABABB]">
						Scan this QR code to get the wallet address.
					</DialogDescription>
				</DialogHeader>

				<div className="">

					{walletAddressQR ? (
						<div className="text-center flex flex-col justify-between h-full max-w-[380px] mx-auto">
							<div className="bg-[#212124] rounded-[16px] px-9 pt-[70px] pb-6">
								<Card className="bg-white border-0 rounded-[16px] shadow-none">
									<img
										src={walletAddressQR}
										alt="Wallet QR Code"
										className="w-full h-[280px] mt-4 rounded-[16px]"
									/>
								</Card>
								<p className="text-sm font-medium text-start leading-4 font-figtree text-[#ffffff] mb-1 mt-6">Your Wallet Address:</p>
								<div className="w-full text-start">
									<span className="wr-address-pop text-xs leading-4 font-normal font-figtree text-[#BABABB]">{walletAddress}</span>
								</div>
							</div>
							<div className="flex gap-2">
								<Button
									variant="default"
									className="flex-1 bg-[#EF4444] h-[42px] rounded-[21px] shadow-[0px_4px_36px_0px_#DC262699]"
									onClick={() => copyAddress(walletAddress)}
								>
									<Copy className="h-4 w-4 mr-2" />
									Copy Address
								</Button>
								{/* <Button
								variant="outline"
								className="flex-1 bg-[#2F2F32] border-[#4C4C4C] text-[#ffffff] hover:bg-[#4C4C4C] hover:text-[#ffffff]"
								onClick={shareAddress}
							>
								<Share2 className="h-4 w-4 mr-2" />
								Share
							</Button> */}
							</div>
						</div>
					) : (
						<div className="w-48 h-48 flex items-center justify-center">
							<Loader2 className="h-8 w-8 animate-spin text-[#BABABB]" />
						</div>
					)}


				</div>
			</DialogContent>
		</Dialog>
	)
}

export default WalletQRModal
